import React, { Component } from 'react';
import { Table, Button, Form, Modal, Spinner, Card, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Httpconfig from '../helpers/HttpconfigAdmin';
import Constant from '../../constants';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment-timezone';
import { normalizeExpiryDate, formatExpiryDateDisplay } from '../../helpers/dateUtils';
import ProductApprovalRow from './ProductApprovalRow';
import { adminPath, getAdminRole, landingForRole } from '../../helpers/adminRoles';

class ProductApprovalDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      retailer: null,
      products: [],
      originalProducts: [], // Store original values to detect changes
      // Pagination
      currentPage: 1,
      pageSize: 100,
      pageSizes: [10, 20, 50, 100, 250, 500],
      editingProduct: null,
      isLoading: true,
      isSaving: false,
      showApproveModal: false,
      productToApprove: null,
      adminId: 1, // Get from auth context
      changedFields: {}, // Track which fields were actually changed
      selectedProducts: [], // Track selected product IDs for bulk actions
      showBulkApproveModal: false,
      bulkProgress: null, // { done, total } while a chunked bulk approve is running
      showBulkRejectModal: false,
      bulkRejectReason: ''
    };
  }

  componentDidMount() {
    const retailerId = this.props.match.params.retailerId;
    if (retailerId) {
      this.fetchRetailerProducts(retailerId, this.props.match.params.date);
    }
  }

  fetchRetailerProducts = async (retailerId, date) => {
    try {
      this.setState({ isLoading: true });

      const dateFilter = date ? `&date=${encodeURIComponent(date)}` : '';
      const response = await Httpconfig.httptokenget(
        `${Constant.siteurl}api/product-approvals/retailer/${retailerId}/products?status=pending${dateFilter}`
      );

      if (response.data.status === 200 && response.data.error === false) {
        // Normalize expiry dates in the products
        const products = (response.data.data.products || []).map(product => {
          const normalizedExpiry = normalizeExpiryDate(product.expiry_date);
          const normalizedOriginalExpiry = normalizeExpiryDate(product.original_expiry_date);
          
          return {
            ...product,
            expiry_date: normalizedExpiry || product.expiry_date || '',
            original_expiry_date: normalizedOriginalExpiry || normalizedExpiry || product.expiry_date || ''
          };
        });
        
        console.log(`[ADMIN-FETCH] Loaded ${products.length} products with normalized dates`);

        this.setState({
          retailer: response.data.data.retailer,
          products: products,
          originalProducts: JSON.parse(JSON.stringify(products)), // Deep clone
          isLoading: false
        });
      } else {
        toast.error('Error loading products');
        this.setState({ isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching retailer products:', error);
      toast.error('Error loading retailer products');
      this.setState({ isLoading: false });
    }
  };

  handleFieldEdit = (productId, field, value) => {
    // Normalize expiry date on edit
    let normalizedValue = value;
    if (field === 'expiry_date' && value) {
      // If value is in YYYY-MM format (from month input), convert to MM/YYYY
      if (/^\d{4}-\d{2}$/.test(value)) {
        const [year, month] = value.split('-');
        normalizedValue = `${month}/${year}`;
        console.log(`[ADMIN-EDIT] Converted month input: "${value}" -> "${normalizedValue}"`);
      } else {
        // Try to normalize any other format
        const normalized = normalizeExpiryDate(value);
        if (normalized) {
          normalizedValue = normalized;
          console.log(`[ADMIN-EDIT] Normalized expiry: "${value}" -> "${normalizedValue}"`);
        } else if (value.trim() !== '') {
          // Show warning if date format is invalid
          toast.warning('Expiry date format should be MM/YYYY (e.g., 03/2025)', { autoClose: 3000 });
          console.warn(`[ADMIN-EDIT] Invalid expiry date format: "${value}"`);
        }
      }
    }
    
    const updatedProducts = this.state.products.map(p => {
      if (p.id === productId) {
        return { ...p, [field]: normalizedValue };
      }
      return p;
    });
    
    // Mark this field as changed for this product
    const changedFields = { ...this.state.changedFields };
    if (!changedFields[productId]) {
      changedFields[productId] = new Set();
    }
    changedFields[productId].add(field);
    
    this.setState({ products: updatedProducts, changedFields });
  };

  hasFieldChanged = (productId, field) => {
    const originalProduct = this.state.originalProducts.find(p => p.id === productId);
    const currentProduct = this.state.products.find(p => p.id === productId);
    
    if (!originalProduct || !currentProduct) return false;
    
    return originalProduct[field] !== currentProduct[field];
  };

  handleSaveProduct = async (product) => {
    // Check if any field actually changed
    const hasChanges = this.state.changedFields[product.id] && this.state.changedFields[product.id].size > 0;
    
    if (!hasChanges) {
      return; // Don't save if nothing changed
    }

    try {
      this.setState({ isSaving: true });
      
      const response = await Httpconfig.httptokenput(
        `${Constant.siteurl}api/product-approvals/update/${product.id}`,
        {
          medicinename: product.medicinename,
          manufacturer: product.manufacturer,
          strength: product.strength,
          size: product.size,
          packaging: product.packaging || '',
          composition: product.composition,
          form: product.form,
          description: product.description,
          hsn_code: product.hsn_code,
          batch: product.batch,
          expiry_date: product.expiry_date,
          quantity: product.quantity,
          mrp: product.mrp,
          admin_notes: product.admin_notes
        }
      );

      if (response.data.status === 200 && response.data.error === false) {
        toast.success('Product updated successfully');
        
        // Update original products and clear changed fields
        const updatedOriginalProducts = this.state.originalProducts.map(p => 
          p.id === product.id ? { ...product } : p
        );
        
        const changedFields = { ...this.state.changedFields };
        delete changedFields[product.id];
        
        this.setState({ 
          isSaving: false,
          originalProducts: updatedOriginalProducts,
          changedFields
        });
      } else {
        toast.error('Error updating product');
        this.setState({ isSaving: false });
      }
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Error updating product');
      this.setState({ isSaving: false });
    }
  };

  handleApproveProduct = async (product) => {
    try {
      this.setState({ isSaving: true });
      
      const response = await Httpconfig.httptokenpost(
        `${Constant.siteurl}api/product-approvals/approve/${product.id}`,
        { admin_id: this.state.adminId }
      );

      if (response.data.status === 200 && response.data.error === false) {
        toast.success(
          `Product approved! Medicine ID: ${response.data.data.medicine_id}`,
          { autoClose: 8000 }
        );
        
        // Remove approved product from list
        const remainingProducts = this.state.products.filter(p => p.id !== product.id);
        const remainingOriginalProducts = this.state.originalProducts.filter(p => p.id !== product.id);
        
        // Remove from selected if it was selected
        const updatedSelectedProducts = this.state.selectedProducts.filter(id => id !== product.id);
        
        this.setState({ 
          products: remainingProducts,
          originalProducts: remainingOriginalProducts,
          selectedProducts: updatedSelectedProducts,
          isSaving: false,
          showApproveModal: false,
          productToApprove: null
        });

        // If no more products for this date, go back to the retailer's date list
        // (other submission dates for the same retailer may still be pending)
        if (remainingProducts.length === 0) {
          toast.info('All products approved! Returning to date list...', { autoClose: 3000 });
          setTimeout(() => {
            this.props.history.push(adminPath(`product-approvals/retailer/${this.props.match.params.retailerId}`));
          }, 2000);
        }
      } else {
        toast.error('Error approving product');
        this.setState({ isSaving: false });
      }
    } catch (error) {
      console.error('Error approving product:', error);
      toast.error(error.response?.data?.message || 'Error approving product');
      this.setState({ isSaving: false });
    }
  };

  downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  getFilenameFromDisposition = (disposition) => {
    if (!disposition) return null;
    const fileNameMatch = /filename\*?=(?:UTF-8'')?"?([^";]*)"?/i.exec(disposition);
    return fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : null;
  };

  handleRejectProduct = async (product) => {
    const reason = prompt('Enter reason for rejection:');
    if (!reason) return;

    try {
      this.setState({ isSaving: true });
      
      const response = await Httpconfig.httptokenpostDownload(
        `${Constant.siteurl}api/product-approvals/reject/${product.id}?download=true`,
        { 
          admin_id: this.state.adminId,
          admin_notes: reason
        }
      );

      // Attempt to download file if response is a blob
      if (response.data && response.data instanceof Blob) {
        const disposition = response.headers['content-disposition'];
        const filename = this.getFilenameFromDisposition(disposition) || `rejected_product_${product.id}.xlsx`;
        this.downloadBlob(response.data, filename);
      }

      // Regardless of download, update UI to remove product
      toast.success('Product rejected');
      const remainingProducts = this.state.products.filter(p => p.id !== product.id);
      const remainingOriginalProducts = this.state.originalProducts.filter(p => p.id !== product.id);
      const updatedSelectedProducts = this.state.selectedProducts.filter(id => id !== product.id);

      this.setState({ 
        products: remainingProducts,
        originalProducts: remainingOriginalProducts,
        selectedProducts: updatedSelectedProducts,
        isSaving: false 
      });
    } catch (error) {
      console.error('Error rejecting product:', error);
      toast.error('Error rejecting product');
      this.setState({ isSaving: false });
    }
  };

  showApproveConfirmation = (product) => {
    this.setState({ 
      showApproveModal: true, 
      productToApprove: product 
    });
  };

  hideApproveConfirmation = () => {
    this.setState({ 
      showApproveModal: false, 
      productToApprove: null 
    });
  };

  confirmApproval = () => {
    if (this.state.productToApprove) {
      this.handleApproveProduct(this.state.productToApprove);
    }
  };

  // Bulk selection methods
  handleSelectProduct = (productId) => {
    const { selectedProducts } = this.state;
    const newSelection = selectedProducts.includes(productId)
      ? selectedProducts.filter(id => id !== productId)
      : [...selectedProducts, productId];
    
    this.setState({ selectedProducts: newSelection });
  };

  handleSelectAll = () => {
    // Select/Deselect only products on current page
    const { selectedProducts, products, currentPage, pageSize } = this.state;
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    const pageProducts = products.slice(start, end).map(p => p.id);

    const allOnPageSelected = pageProducts.every(id => selectedProducts.includes(id));
    if (allOnPageSelected) {
      // Deselect page-level products
      this.setState({ selectedProducts: selectedProducts.filter(id => !pageProducts.includes(id)) });
    } else {
      // Add page-level products (avoid duplicates)
      const newSelection = Array.from(new Set([...selectedProducts, ...pageProducts]));
      this.setState({ selectedProducts: newSelection });
    }
  };

  handleChangePageSize = (e) => {
    const newSize = Number(e.target.value) || 10;
    this.setState({ pageSize: newSize, currentPage: 1 });
  };

  handlePageChange = (page) => {
    const totalPages = Math.max(1, Math.ceil(this.state.products.length / this.state.pageSize));
    const newPage = Math.max(1, Math.min(page, totalPages));
    this.setState({ currentPage: newPage });
  };

  getPaginatedProducts = () => {
    const { products, currentPage, pageSize } = this.state;
    const start = (currentPage - 1) * pageSize;
    return products.slice(start, start + pageSize);
  };

  // Bulk approve
  handleBulkApprove = async () => {
    const { selectedProducts, products } = this.state;
    
    if (selectedProducts.length === 0) {
      toast.warning('Please select at least one product');
      return;
    }

    this.setState({ showBulkApproveModal: true });
  };

  // The server wraps each bulk-approve request in a single transaction, so sending
  // every selected id at once means long-held row locks and an all-or-nothing
  // rollback. Chunking keeps each transaction short and lets partial progress
  // survive a chunk that fails.
  BULK_APPROVE_CHUNK_SIZE = 50;

  confirmBulkApproval = async () => {
    const selectedIds = this.state.selectedProducts;
    const chunks = [];
    for (let i = 0; i < selectedIds.length; i += this.BULK_APPROVE_CHUNK_SIZE) {
      chunks.push(selectedIds.slice(i, i + this.BULK_APPROVE_CHUNK_SIZE));
    }

    this.setState({ isSaving: true, bulkProgress: { done: 0, total: selectedIds.length } });

    const approved = [];
    const failed = [];
    let erroredChunks = 0;

    for (const chunk of chunks) {
      try {
        const response = await Httpconfig.httptokenpost(
          `${Constant.siteurl}api/product-approvals/bulk-approve`,
          {
            product_ids: chunk,
            admin_id: this.state.adminId
          }
        );

        if (response.data && response.data.status === 200 && response.data.error === false) {
          const results = response.data.data;
          approved.push(...(results.approved || []));
          failed.push(...(results.failed || []));
        } else {
          // Whole chunk rolled back server-side; those products are still pending.
          erroredChunks++;
          failed.push(...chunk);
        }
      } catch (error) {
        console.error('Error approving batch:', error);
        erroredChunks++;
        failed.push(...chunk);
      }

      this.setState({
        bulkProgress: { done: approved.length + failed.length, total: selectedIds.length }
      });
    }

    // Drop approved rows; keep failures on screen and still selected so the admin
    // can fix the missing fields and retry without hunting for them.
    const approvedSet = new Set(approved);
    const remainingProducts = this.state.products.filter(p => !approvedSet.has(p.id));
    const remainingOriginalProducts = this.state.originalProducts.filter(p => !approvedSet.has(p.id));
    const remainingIds = new Set(remainingProducts.map(p => p.id));

    this.setState({
      products: remainingProducts,
      originalProducts: remainingOriginalProducts,
      selectedProducts: failed.filter(id => remainingIds.has(id)),
      isSaving: false,
      showBulkApproveModal: false,
      bulkProgress: null
    });

    const successCount = approved.length;
    const failCount = failed.length;
    const errorNote = erroredChunks > 0 ? ' Some batches failed on the server and were rolled back.' : '';

    if (successCount === 0 && failCount > 0) {
      toast.error(
        `Bulk approval failed. ${failCount} product(s) could not be approved.${errorNote}`,
        { autoClose: 8000 }
      );
    } else if (successCount > 0 && failCount > 0) {
      toast.warning(
        `${successCount} product(s) approved. ${failCount} failed.${errorNote}`,
        { autoClose: 8000 }
      );
    } else {
      toast.success(
        `${successCount} product(s) approved successfully!`,
        { autoClose: 8000 }
      );
    }

    // If no more products for this date, go back to the retailer's date list
    // (other submission dates for the same retailer may still be pending)
    if (remainingProducts.length === 0) {
      toast.info('All products approved! Returning to date list...', { autoClose: 3000 });
      setTimeout(() => {
        this.props.history.push(adminPath(`product-approvals/retailer/${this.props.match.params.retailerId}`));
      }, 2000);
    }
  };

  // Bulk reject
  handleBulkReject = () => {
    const { selectedProducts } = this.state;
    
    if (selectedProducts.length === 0) {
      toast.warning('Please select at least one product');
      return;
    }

    this.setState({ showBulkRejectModal: true, bulkRejectReason: '' });
  };

  confirmBulkReject = async () => {
    const { selectedProducts, bulkRejectReason } = this.state;
    
    if (!bulkRejectReason.trim()) {
      toast.warning('Please enter a reason for rejection');
      return;
    }

    try {
      this.setState({ isSaving: true });
      
      // Step 1: Reject products on the server and get JSON result so we can update UI immediately
      const response = await Httpconfig.httptokenpost(
        `${Constant.siteurl}api/product-approvals/bulk-reject`,
        { 
          product_ids: selectedProducts,
          admin_id: this.state.adminId,
          admin_notes: bulkRejectReason
        }
      );

      if (response.data && response.data.status === 200 && response.data.error === false) {
        const results = response.data.data;
        const successCount = results.rejected.length;
        const failCount = results.failed.length;

        toast.success(
          `${successCount} product(s) rejected successfully!` +
          (failCount > 0 ? ` ${failCount} failed.` : '')
        );

        // Remove rejected products from list
        const remainingProducts = this.state.products.filter(
          p => !results.rejected.includes(p.id)
        );
        const remainingOriginalProducts = this.state.originalProducts.filter(
          p => !results.rejected.includes(p.id)
        );

        this.setState({ 
          products: remainingProducts,
          originalProducts: remainingOriginalProducts,
          selectedProducts: [],
          isSaving: false,
          showBulkRejectModal: false,
          bulkRejectReason: ''
        });

        // Step 2: kick off file download (non-blocking)
        (async () => {
          try {
            const downloadRes = await Httpconfig.httptokenpostDownload(
              `${Constant.siteurl}api/product-approvals/bulk-reject?download=true`,
              { 
                product_ids: selectedProducts,
                admin_id: this.state.adminId,
                admin_notes: bulkRejectReason
              }
            );

            if (downloadRes.data && downloadRes.data instanceof Blob) {
              const disposition = downloadRes.headers['content-disposition'];
              const filename = this.getFilenameFromDisposition(disposition) || `rejected_products_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
              this.downloadBlob(downloadRes.data, filename);
            }
          } catch (downloadErr) {
            console.warn('Bulk reject report download failed:', downloadErr);
          }
        })();

        return;
      }

      toast.error('Error rejecting products');
      this.setState({ isSaving: false });
    } catch (error) {
      console.error('Error bulk rejecting products:', error);
      toast.error('Error rejecting products');
      this.setState({ isSaving: false });
    }
  };

  render() {
    const { retailer, products, isLoading, isSaving, showApproveModal, productToApprove, selectedProducts, showBulkApproveModal, showBulkRejectModal, bulkRejectReason, currentPage, pageSize, pageSizes, bulkProgress } = this.state;

    const totalPages = Math.max(1, Math.ceil(products.length / pageSize));
    const paginatedProducts = this.getPaginatedProducts();
    const pageProductIds = paginatedProducts.map(p => p.id);
    // Set lookup keeps selection checks O(1); `includes` per row made this O(n^2),
    // which is ~750k scans per render once a 500-row page is fully selected.
    const selectedSet = new Set(selectedProducts);
    const allOnPageSelected = pageProductIds.length > 0 && pageProductIds.every(id => selectedSet.has(id));

    if (isLoading) {
      return (
        <div className="container-fluid">
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading retailer products...</p>
          </div>
        </div>
      );
    }

    if (!retailer) {
      return (
        <div className="container-fluid">
          <div className="alert alert-danger">Retailer not found</div>
        </div>
      );
    }

    return (
      <>
      <style>{`
        /* ============================================================
           Product approval grid.

           The grid carries 13 columns of editable data and must never
           scroll horizontally, so width is a fixed budget rather than
           something the browser negotiates: table-layout:fixed plus
           the colgroup in the markup decide every column, and cell
           content can no longer squeeze its neighbours. Percentages
           (not px) hold the same proportions at every viewport, and the
           page runs container-fluid so the budget has room to spend.
           ============================================================ */

        /* A data grid deserves the whole screen; the 1140px .container
           this page used to sit in was the main source of the squeeze. */
        .pa-page {
          max-width: 1840px;
          margin: 0 auto;
          padding-left: 24px;
          padding-right: 24px;
        }

        /* The ID selector matches the #reg_form the panel shares, because
           the global stylesheet pads that ID by 10px - inside a bordered
           card that reads as a white gutter around the toolbar. */
        #reg_form.pa-panel {
          padding: 0;
          background: #ffffff;
          border: 1px solid #e4e7ec;
          border-radius: 10px;
          box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
          overflow: hidden;
        }

        .pa-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          padding: 14px 18px;
          background: #f9fafb;
          border-bottom: 1px solid #e4e7ec;
        }
        .pa-toolbar-title {
          margin: 0;
          font-size: 15px;
          font-weight: 600;
          color: #101828;
          letter-spacing: 0.1px;
        }
        .pa-toolbar-count {
          display: inline-block;
          margin-left: 8px;
          padding: 3px 10px;
          border-radius: 20px;
          background: #eaeaf7;
          color: #2E3192;
          font-size: 12px;
          font-weight: 600;
        }
        /* The selection pill has to win against the total-count pill
           sitting right next to it, or the two read as one blob. */
        .pa-toolbar-count.is-selection {
          background: #2E3192;
          color: #ffffff;
        }
        .pa-toolbar-group {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .pa-toolbar-label {
          margin: 0;
          font-size: 12px;
          color: #667085;
          white-space: nowrap;
        }

        /* The vertical scrollbar is deliberately chunky - it is the only
           scrollbar on the panel, so it doubles as the position cue.
           overflow-x is pinned to hidden: with a fixed layout nothing
           should ever overflow sideways, and this guarantees it. */
        .approval-table-scroll {
          max-height: 70vh;
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: auto;
          scrollbar-color: #adb5bd #f1f1f1;
        }
        .approval-table-scroll::-webkit-scrollbar {
          width: 14px;
        }
        .approval-table-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .approval-table-scroll::-webkit-scrollbar-thumb {
          background: #adb5bd;
          border-radius: 7px;
          border: 3px solid #f1f1f1;
        }
        .approval-table-scroll::-webkit-scrollbar-thumb:hover {
          background: #868e96;
        }

        .approval-table-scroll .pa-table {
          table-layout: fixed;
          width: 100%;
          margin-bottom: 0;
          border-collapse: separate;
          border-spacing: 0;
          background: #ffffff;
        }

        /* Sticky header. Because the cells are what stick, the brand
           colour is painted per-cell - a background on <thead> alone
           leaves the first and last columns blank while scrolling. */
        .approval-table-scroll .pa-table thead th {
          position: sticky;
          top: 0;
          z-index: 10;
          background-color: #2E3192;
          color: #ffffff;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding: 12px 10px;
          border: 0;
          vertical-align: middle;
          box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.12);
        }
        .approval-table-scroll .pa-table thead th.pa-th-center {
          text-align: center;
          padding-left: 4px;
          padding-right: 4px;
        }
        .approval-table-scroll .pa-table thead th.pa-th-num {
          text-align: right;
        }

        /* Rows: a light zebra for tracking across 13 columns, a warmer
           hover, and a left accent bar on the selected row so selection
           reads even where the tint alone is subtle. */
        .approval-table-scroll .pa-row > td {
          height: 68px;
          /* Bootstrap 4 draws its row rule as a border-top on every cell.
             With border-collapse: separate that would stack against the
             rule below, so the grid owns the line and clears theirs. */
          border-top: 0;
          border-bottom: 1px solid #eef0f4;
          vertical-align: middle;
          background: #ffffff;
        }
        .approval-table-scroll .pa-row:nth-child(even) > td {
          background: #fbfcfd;
        }
        .approval-table-scroll .pa-row:hover > td {
          background: #f4f7ff;
        }
        .approval-table-scroll .pa-row.is-selected > td {
          background: #e8eefc;
        }
        .approval-table-scroll .pa-row.is-selected > td:first-child {
          box-shadow: inset 3px 0 0 #2E3192;
        }

        /* Every column reserves the same strip beneath its content. The
           expiry column carries a verdict badge under its input; without
           the reserve that one cell is 21px taller than the other twelve,
           and vertical-align: middle then centres its input 10px above the
           line every other input in the row sits on. With the reserve all
           thirteen cells are the same height, so one middle-alignment puts
           every control on the same line. */
        .approval-table-scroll .pa-cell {
          padding: 6px 10px;
        }
        .approval-table-scroll .pa-cell::after {
          content: '';
          display: block;
          height: 17px;
          margin-top: 4px;
        }
        /* The expiry cell fills that reserve with the real strip instead. */
        .approval-table-scroll .pa-cell-expiry::after {
          content: none;
        }
        .approval-table-scroll .pa-expiry-strip {
          height: 17px;
          margin-top: 4px;
          line-height: 17px;
        }
        .approval-table-scroll .pa-cell-check,
        .approval-table-scroll .pa-cell-index {
          text-align: center;
          padding-left: 2px;
          padding-right: 2px;
        }
        .approval-table-scroll .pa-cell-index {
          font-size: 12px;
          font-weight: 600;
          color: #667085;
          white-space: nowrap;
        }
        .approval-table-scroll .pa-cell-actions {
          padding-left: 4px;
          padding-right: 4px;
        }

        /* Read-only reference value. It wraps in full rather than
           clamping - the reviewer compares it against the editable name
           beside it, so it must never be cut off. */
        .approval-table-scroll .pa-cell-original {
          font-size: 12px;
        }
        .approval-table-scroll .pa-original-name {
          color: #475467;
          font-weight: 500;
          line-height: 1.35;
          overflow-wrap: anywhere;
        }
        .approval-table-scroll .pa-original-expiry {
          margin-top: 3px;
          font-size: 11px;
          color: #98a2b3;
        }

        /* One input metric for the whole grid. Equal heights are what
           make a dense row scan as a single line instead of a jumble. */
        .approval-table-scroll .pa-input.form-control {
          width: 100%;
          min-width: 0;
          height: 34px;
          padding: 4px 9px;
          font-size: 13px;
          color: #1f2937;
          background-color: #ffffff;
          border: 1px solid #d6dae1;
          border-radius: 6px;
          box-shadow: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .approval-table-scroll .pa-input.form-control:hover {
          border-color: #b9c0cb;
        }
        .approval-table-scroll .pa-input.form-control:focus {
          border-color: #00B7CE;
          box-shadow: 0 0 0 3px rgba(0, 183, 206, 0.18);
        }
        .approval-table-scroll .pa-input.form-control::placeholder {
          color: #b6bcc7;
        }
        .approval-table-scroll .pa-input-strong.form-control {
          font-weight: 600;
          color: #101828;
        }
        .approval-table-scroll .pa-input-num.form-control {
          text-align: right;
          font-variant-numeric: tabular-nums;
        }
        .approval-table-scroll .pa-select.form-control {
          padding-right: 4px;
          cursor: pointer;
        }
        .approval-table-scroll .pa-input.is-expired {
          background-color: #fff4f4;
          border-color: #f1b0b7;
        }

        /* The expiry verdict sits in normal flow under its input. It used
           to be absolutely positioned, which forced 24px of bottom padding
           onto every cell in the table just to reserve the strip - that
           padding is what made the rows feel bloated and misaligned. */
        .approval-table-scroll .pa-expiry-note {
          display: inline-block;
          vertical-align: top;
          padding: 0 7px;
          border-radius: 10px;
          font-size: 10.5px;
          font-weight: 600;
          line-height: 15px;
          letter-spacing: 0.2px;
          white-space: nowrap;
        }
        .approval-table-scroll .pa-expiry-red {
          color: #b42318;
          background: #fef3f2;
          border: 1px solid #fecdca;
        }
        .approval-table-scroll .pa-expiry-orange {
          color: #b54708;
          background: #fffaeb;
          border: 1px solid #fedf89;
        }
        .approval-table-scroll .pa-expiry-green {
          color: #067647;
          background: #ecfdf3;
          border: 1px solid #abefc6;
        }

        /* Centre the checkbox in its column instead of leaving it on the
           bootstrap label indent. */
        .approval-table-scroll .form-check {
          padding-left: 0;
          margin: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: auto;
        }
        .approval-table-scroll .form-check-input {
          position: static;
          margin: 0;
          float: none;
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .approval-table-scroll .pa-actions {
          display: flex;
          gap: 6px;
          justify-content: center;
        }
        /* Two saturated blocks on every one of 500 rows turned the grid
           into a traffic light. The buttons now sit as quiet tinted
           chips and only go solid under the cursor, so colour marks the
           row you are acting on rather than all of them at once. */
        .approval-table-scroll .pa-action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          padding: 0;
          border: 1px solid transparent;
          border-radius: 6px;
          background: transparent;
          font-size: 12px;
          line-height: 1;
          transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
        }
        .approval-table-scroll .pa-action-approve {
          background-color: #ecfdf3;
          border-color: #abefc6;
          color: #067647;
        }
        .approval-table-scroll .pa-action-reject {
          background-color: #fef3f2;
          border-color: #fecdca;
          color: #b42318;
        }
        .approval-table-scroll .pa-action:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .approval-table-scroll .pa-action-approve:not(:disabled):hover {
          background-color: #12a150;
          border-color: #12a150;
          color: #ffffff;
        }
        .approval-table-scroll .pa-action-reject:not(:disabled):hover {
          background-color: #e5484d;
          border-color: #e5484d;
          color: #ffffff;
        }
        .approval-table-scroll .pa-action:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(0, 183, 206, 0.25);
        }

        /* ---------- Toolbar controls ------------------------------- */
        .pa-toolbar .pa-toolbar-select.form-control {
          width: 128px;
          height: 32px;
          padding: 2px 8px;
          font-size: 12.5px;
          color: #344054;
          border: 1px solid #d6dae1;
          border-radius: 6px;
          box-shadow: none;
          cursor: pointer;
        }
        .pa-toolbar .pa-toolbar-select.form-control:focus {
          border-color: #00B7CE;
          box-shadow: 0 0 0 3px rgba(0, 183, 206, 0.18);
        }
        .pa-toolbar .btn {
          height: 32px;
          padding: 0 12px;
          font-size: 12.5px;
          font-weight: 600;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        /* ---------- Footer / pagination -----------------------------
           Bootstrap's default primary blue on the active page was the
           one colour on the screen that belonged to no palette here, so
           the pager carries the brand indigo instead. */
        .pa-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          padding: 12px 18px;
          background: #f9fafb;
          border-top: 1px solid #e4e7ec;
        }
        .pa-footer-count {
          font-size: 12.5px;
          color: #667085;
        }
        .pa-footer-count strong {
          color: #344054;
          font-weight: 600;
        }
        .pa-pager {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
        }
        .pa-pager .pa-page-btn.btn {
          min-width: 32px;
          height: 32px;
          padding: 0 9px;
          font-size: 12.5px;
          font-weight: 600;
          color: #475467;
          background: #ffffff;
          border: 1px solid #d6dae1;
          border-radius: 6px;
          box-shadow: none;
          transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
        }
        .pa-pager .pa-page-btn.btn:not(:disabled):hover {
          background: #f2f4f7;
          border-color: #b9c0cb;
          color: #101828;
        }
        .pa-pager .pa-page-btn.btn:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(0, 183, 206, 0.25);
        }
        .pa-pager .pa-page-btn.is-active.btn,
        .pa-pager .pa-page-btn.is-active.btn:not(:disabled):hover {
          background: #2E3192;
          border-color: #2E3192;
          color: #ffffff;
        }
        .pa-pager .pa-page-btn.btn:disabled {
          opacity: 0.5;
          color: #98a2b3;
        }
        .pa-pager-gap {
          padding: 0 4px;
          color: #98a2b3;
          font-size: 13px;
          letter-spacing: 1px;
        }

        /* Narrower screens keep all 13 columns - the budget just spends
           less on padding and type, so nothing has to be dropped. */
        @media (max-width: 1600px) {
          .pa-page { padding-left: 16px; padding-right: 16px; }
          .approval-table-scroll .pa-cell { padding: 6px 7px; }
          .approval-table-scroll .pa-input.form-control { font-size: 12.5px; padding: 4px 7px; }
        }
        @media (max-width: 1300px) {
          .approval-table-scroll .pa-table thead th {
            padding: 10px 6px;
            font-size: 10px;
            letter-spacing: 0.2px;
          }
          .approval-table-scroll .pa-cell { padding: 5px 5px; }
          .approval-table-scroll .pa-row > td { height: 64px; }
          .approval-table-scroll .pa-input.form-control {
            height: 32px;
            font-size: 12px;
            padding: 3px 6px;
          }
          .approval-table-scroll .pa-cell-original { font-size: 11.5px; }
          .approval-table-scroll .pa-action { width: 27px; height: 27px; font-size: 11px; }
          .approval-table-scroll .pa-actions { gap: 4px; }
        }
      `}</style>
      <section id="main_dashboard">
        <div className="container-fluid pa-page" id="main_front">
          <div className="row">
            <div className="col-md-12">
              <div className="dash-section">
                <div className="section-header">
                  <ol className="breadcrumb">
                    <li className="active">
                      <Link to={landingForRole(getAdminRole())}>Dashboard</Link> &gt;{' '}
                      <Link to={adminPath('product-approvals')}>Product Approvals</Link> &gt;{' '}
                      <Link to={adminPath(`product-approvals/retailer/${this.props.match.params.retailerId}`)}>
                        {retailer ? retailer.name : 'Select Date'}
                      </Link> &gt;{' '}
                      <a>Review Products</a>
                    </li>
                  </ol>
                </div>
              </div>

              <section id="CMS_tab">
                <div className="CMS_content">
                  <div className="row">
                      <div className="tab-header" style={{ borderBottom: '2px solid #e0e0e0', paddingBottom: '15px', marginBottom: '25px' }}>
                        <h3 style={{ fontSize: '24px', fontWeight: 600, color: '#2c3e50', marginBottom: '5px' }}>Product Approval Review</h3>
                        <p style={{ color: '#7f8c8d', fontSize: '14px', marginBottom: 0 }}>
                          Review and approve products from {retailer.name}
                          {this.props.match.params.date && ` — submitted ${moment(this.props.match.params.date).format('DD MMM YYYY')}`}
                        </p>
                      </div>

                      {/* Retailer Info Section */}
                      <div className="col-md-12 mb-4">
                        <div style={{
                          backgroundColor: '#d1ecf1',
                          border: '1px solid #bee5eb',
                          borderRadius: '5px',
                          padding: '20px'
                        }}>
                          <div className="row align-items-center">
                            <div className="col-md-9">
                              <h4 style={{ fontWeight: 700, color: '#0c5460', marginBottom: '12px', fontSize: '20px' }}>
                                🏪 {retailer.name}
                              </h4>
                              <div style={{ fontSize: '14px', color: '#0c5460', marginBottom: '8px' }}>
                                📍 <strong>Address:</strong> {retailer.address}
                              </div>
                              <div className="d-flex gap-4" style={{ fontSize: '14px', color: '#0c5460' }}>
                                <span><strong>📧 Email:</strong> {retailer.email}</span>
                                <span><strong>📱 Phone:</strong> {retailer.phone}</span>
                                <span><strong>🏢 GST:</strong> {retailer.gst_number}</span>
                              </div>
                            </div>
                            <div className="col-md-3 text-end">
                              <div style={{
                                backgroundColor: '#ffc107',
                                color: '#fff',
                                padding: '20px',
                                borderRadius: '10px',
                                display: 'inline-block',
                                fontWeight: 700,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                              }}>
                                <div style={{ fontSize: '32px', lineHeight: 1 }}>{products.length}</div>
                                <div style={{ fontSize: '14px', marginTop: '8px' }}>Pending Products</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Products Table */}
                      <div className="col-md-12">
                        {products.length === 0 ? (
                          <div style={{
                            backgroundColor: '#d4edda',
                            border: '1px solid #c3e6cb',
                            borderRadius: '5px',
                            padding: '40px',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '48px', color: '#28a745', marginBottom: '15px' }}>✓</div>
                            <p style={{ fontSize: '16px', color: '#155724', marginBottom: 0 }}>No pending products to review</p>
                          </div>
                        ) : (
                          <div id="reg_form" className="pa-panel">
                            <div className="pa-toolbar">
                              <h5 className="pa-toolbar-title">
                                Product Approval Requests
                                <span className="pa-toolbar-count">{products.length}</span>
                                {selectedProducts.length > 0 && (
                                  <span className="pa-toolbar-count is-selection">{selectedProducts.length} selected</span>
                                )}
                              </h5>
                              {selectedProducts.length > 0 && (
                                <div className="pa-toolbar-group">
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={this.handleBulkApprove}
                                    disabled={isSaving}
                                  >
                                    <i className="fa fa-check-circle"></i> Approve Selected ({selectedProducts.length})
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={this.handleBulkReject}
                                    disabled={isSaving}
                                  >
                                    <i className="fa fa-times"></i> Reject Selected ({selectedProducts.length})
                                  </Button>
                                </div>
                              )}
                              <div className="pa-toolbar-group">
                                <label className="pa-toolbar-label">Show:</label>
                                <Form.Control as="select" size="sm" className="pa-toolbar-select" value={pageSize} onChange={this.handleChangePageSize}>
                                  {pageSizes.map(s => (
                                    <option key={s} value={s}>{s} per page</option>
                                  ))}
                                </Form.Control>
                              </div>
                            </div>
                            
                            {/* Table with sticky headers */}
                            <div className="dataTables_wrapper approval-table-scroll">
                              <Table className="pa-table">
                                <colgroup>
                                  <col style={{ width: '2.6%' }} />
                                  <col style={{ width: '3.4%' }} />
                                  <col style={{ width: '12%' }} />
                                  <col style={{ width: '14%' }} />
                                  <col style={{ width: '11.5%' }} />
                                  <col style={{ width: '4.5%' }} />
                                  <col style={{ width: '8%' }} />
                                  <col style={{ width: '8%' }} />
                                  <col style={{ width: '7.5%' }} />
                                  <col style={{ width: '11%' }} />
                                  <col style={{ width: '5%' }} />
                                  <col style={{ width: '5.5%' }} />
                                  <col style={{ width: '7%' }} />
                                </colgroup>
                                <thead>
                                  <tr>
                                    <th className="pa-th-center">
                                      <Form.Check
                                        type="checkbox"
                                        checked={allOnPageSelected}
                                        onChange={this.handleSelectAll}
                                        aria-label="Select all products on this page"
                                      />
                                    </th>
                                    <th className="pa-th-center">#</th>
                                    <th>Original Name</th>
                                    <th>Medicine Name</th>
                                    <th>Manufacturer</th>
                                    <th>Size</th>
                                    <th>Packaging</th>
                                    <th>Form</th>
                                    <th>Batch</th>
                                    <th>Expiry Date</th>
                                    <th className="pa-th-num">Qty</th>
                                    <th className="pa-th-num">MRP</th>
                                    <th className="pa-th-center">Actions</th>
                                  </tr>
                                </thead>
                      <tbody>
                        {this.getPaginatedProducts().map((product, idx) => (
                          <ProductApprovalRow
                            key={product.id}
                            product={product}
                            index={((currentPage - 1) * pageSize) + idx}
                            isSelected={selectedSet.has(product.id)}
                            isSaving={isSaving}
                            onFieldEdit={this.handleFieldEdit}
                            onSaveProduct={this.handleSaveProduct}
                            onSelect={this.handleSelectProduct}
                            onApprove={this.showApproveConfirmation}
                            onReject={this.handleRejectProduct}
                          />
                        ))}
                      </tbody>
                    </Table>
                    </div>
                    
                    {/* Pagination controls */}
                    <div className="pa-footer">
                      <div className="pa-footer-count">
                        Showing <strong>{paginatedProducts.length}</strong> of <strong>{products.length}</strong> products
                      </div>
                      <div className="pa-pager">
                        <Button size="sm" variant="light" className="pa-page-btn" onClick={() => this.handlePageChange(currentPage - 1)} disabled={currentPage <= 1}>Previous</Button>
                        {totalPages <= 10 ? (
                          // Show all pages if 10 or fewer
                          [...Array(totalPages)].map((_, i) => (
                            <Button
                              key={i}
                              size="sm"
                              variant="light"
                              className={`pa-page-btn${currentPage === i+1 ? ' is-active' : ''}`}
                              onClick={() => this.handlePageChange(i+1)}
                            >
                              {i+1}
                            </Button>
                          ))
                        ) : (
                          // Show smart pagination for more than 10 pages
                          <>
                            {currentPage > 3 && <Button size="sm" variant="light" className="pa-page-btn" onClick={() => this.handlePageChange(1)}>1</Button>}
                            {currentPage > 4 && <span className="pa-pager-gap">...</span>}
                            {[...Array(5)].map((_, i) => {
                              const pageNum = currentPage - 2 + i;
                              if (pageNum > 0 && pageNum <= totalPages) {
                                return (
                                  <Button
                                    key={pageNum}
                                    size="sm"
                                    variant="light"
                                    className={`pa-page-btn${currentPage === pageNum ? ' is-active' : ''}`}
                                    onClick={() => this.handlePageChange(pageNum)}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              }
                              return null;
                            })}
                            {currentPage < totalPages - 3 && <span className="pa-pager-gap">...</span>}
                            {currentPage < totalPages - 2 && (
                              <Button size="sm" variant="light" className="pa-page-btn" onClick={() => this.handlePageChange(totalPages)}>
                                {totalPages}
                              </Button>
                            )}
                          </>
                        )}
                        <Button size="sm" variant="light" className="pa-page-btn" onClick={() => this.handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages}>Next</Button>
                      </div>
                    </div>
                  </div>
              )}
            </div>
          </div>
        </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      {/* Approve Confirmation Modal */}
      <Modal show={showApproveModal} onHide={this.hideApproveConfirmation} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Product Approval</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {productToApprove && (
              <>
                <p>Are you sure you want to approve this product?</p>
                <div className="bg-light p-3 rounded">
                  <p className="mb-1"><strong>Medicine Name:</strong> {productToApprove.medicinename}</p>
                  <p className="mb-1"><strong>Manufacturer:</strong> {productToApprove.manufacturer}</p>
                  <p className="mb-1"><strong>Batch:</strong> {productToApprove.batch}</p>
                  <p className="mb-1"><strong>Expiry Date:</strong> {formatExpiryDateDisplay(productToApprove.expiry_date)}</p>
                  <p className="mb-0"><strong>MRP:</strong> ₹{productToApprove.mrp}</p>
                </div>
                <div className="alert alert-info mt-3 mb-0">
                  <i className="fa fa-info-circle"></i> This will:
                  <ul className="mb-0 mt-2">
                    <li>Add product to master database</li>
                    <li>Update retailer stock automatically</li>
                    <li>Generate unique medicine ID</li>
                  </ul>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.hideApproveConfirmation} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="success" onClick={this.confirmApproval} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Approving...
                </>
              ) : (
                <>
                  <i className="fa fa-check-circle"></i> Yes, Approve
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Bulk Approve Confirmation Modal */}
        <Modal show={showBulkApproveModal} onHide={() => this.setState({ showBulkApproveModal: false })} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Bulk Approval</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to approve <strong>{selectedProducts.length}</strong> selected product(s)?</p>
            <div className="alert alert-info mb-0">
              <i className="fa fa-info-circle"></i> This will:
              <ul className="mb-0 mt-2">
                <li>Add all selected products to master database</li>
                <li>Update retailer stock automatically</li>
                <li>Generate unique medicine IDs for each</li>
              </ul>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => this.setState({ showBulkApproveModal: false })} 
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button variant="success" onClick={this.confirmBulkApproval} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {bulkProgress ? `Approving ${bulkProgress.done} of ${bulkProgress.total}...` : 'Approving...'}
                </>
              ) : (
                <>
                  <i className="fa fa-check-circle"></i> Approve {selectedProducts.length} Product(s)
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Bulk Reject Confirmation Modal */}
        <Modal show={showBulkRejectModal} onHide={() => this.setState({ showBulkRejectModal: false })} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Bulk Rejection</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to reject <strong>{selectedProducts.length}</strong> selected product(s)?</p>
            <Form.Group className="mb-3">
              <Form.Label>Reason for Rejection *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={bulkRejectReason}
                onChange={(e) => this.setState({ bulkRejectReason: e.target.value })}
                placeholder="Enter reason for rejection (required)"
                disabled={isSaving}
              />
            </Form.Group>
            <div className="alert alert-warning mb-0">
              <i className="fa fa-exclamation-triangle"></i> This action cannot be undone. The retailer will be notified.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => this.setState({ showBulkRejectModal: false, bulkRejectReason: '' })} 
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={this.confirmBulkReject} 
              disabled={isSaving || !bulkRejectReason.trim()}
            >
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Rejecting...
                </>
              ) : (
                <>
                  <i className="fa fa-times"></i> Reject {selectedProducts.length} Product(s)
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
}

export default ProductApprovalDetail;
